import { useEffect } from 'react';
import {
    Autocomplete as MuiAutocomplete,
    Box,
    Chip,
    Divider,
    Stack,
    TextField as MuiTextField,
    Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'FrontRoyalReactHookForm';
import { TextField } from 'FrontRoyalMaterialUiForm';
import { ContentItemImagePreview, ContentItemImageUpload, SectionHeading, type S3Asset } from 'ContentItemEditor';
import { type PlaylistFormValues, type RelatedCohort } from './PlaylistEditor.types';

type Props = {
    image: S3Asset | null | undefined;
    relatedCohorts: RelatedCohort[] | null | undefined;
    familyPlaylistTitles: string[] | null | undefined;
    availableContentFamilyIdentifiers: string[];
    uploadingImage: boolean;
    onUploadImage: (files: File[]) => void;
    onRemoveImage: () => void;
};

// oxlint-disable-next-line max-lines-per-function -- the identity fields of the playlist, in one column
export default function PlaylistHeaderFields({
    image,
    relatedCohorts,
    familyPlaylistTitles,
    availableContentFamilyIdentifiers,
    uploadingImage,
    onUploadImage,
    onRemoveImage,
}: Props) {
    const { control, watch, setFieldValue, trigger } = useFormContext<PlaylistFormValues>();

    const locale = watch('locale');
    const courseCodePrefix = watch('course_code_prefix');
    const courseCode = watch('course_code');

    /*
        update_locale_pack runs only for an English content item, so every locale pack field a
        translation shows would be discarded on save. The Angular page disabled the content family
        identifier alone and let the other three accept edits that went nowhere.
    */
    const localePackEditable = locale === 'en';

    // react-hook-form surfaces only the changed field's errors, so the course code pair needs
    // both revalidated or the message stays on the field the admin is not editing.
    useEffect(() => {
        trigger(['course_code', 'course_code_prefix']);
    }, [courseCode, courseCodePrefix, trigger]);

    const courseCodePreview = `${courseCodePrefix} ${courseCode}`;

    return (
        <Stack spacing={3}>
            {/* The image beside the fields it belongs to, rather than above them: a card this wide
                would otherwise stretch every input across the page. */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
                <Stack spacing={1} sx={{ alignItems: 'center', alignSelf: 'stretch', justifyContent: 'space-between' }}>
                    <ContentItemImagePreview image={image} alt="The playlist image" />

                    <ContentItemImageUpload
                        uploading={uploadingImage}
                        onUpload={onUploadImage}
                        onRemove={image ? onRemoveImage : undefined}
                    />
                </Stack>

                <Stack spacing={3} sx={{ flex: 1, width: '100%' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField name="tag" label="Tag" fullWidth sx={{ flex: 1 }} />
                        <TextField
                            name="title"
                            label="Title"
                            fullWidth
                            slotProps={{ htmlInput: { dir: 'auto' } }}
                            sx={{ flex: 3 }}
                        />
                    </Stack>

                    <TextField
                        name="description"
                        label="Description"
                        fullWidth
                        multiline
                        rows={4}
                        slotProps={{ htmlInput: { dir: 'auto' } }}
                    />
                </Stack>
            </Stack>

            <Divider />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
                <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                    {/* Not form backed: the server has no column for it. */}
                    <MuiTextField
                        name="course_code_preview"
                        label="Course Code Preview"
                        value={courseCodePreview}
                        disabled
                        fullWidth
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            name="course_code_prefix"
                            label="Course Code Prefix"
                            fullWidth
                            disabled={!localePackEditable}
                        />
                        <TextField name="course_code" label="Course Code" fullWidth disabled={!localePackEditable} />
                    </Stack>

                    <TextField
                        name="hours_of_credit"
                        label="Credit Hours"
                        type="number"
                        disabled={!localePackEditable}
                        slotProps={{ htmlInput: { step: 0.25, min: 0 } }}
                    />
                </Stack>

                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

                <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
                    {/* MUI's Autocomplete, not FrontRoyalMaterialUiForm's: that props type pins
                        MUI's FreeSolo generic to false, and an admin can name a new family. */}
                    <Controller
                        control={control}
                        name="content_family_identifier"
                        render={({ field }) => (
                            <MuiAutocomplete
                                freeSolo
                                disabled={!localePackEditable}
                                options={availableContentFamilyIdentifiers}
                                value={field.value || ''}
                                // freeSolo reports typed text only here, and a selection updates
                                // the input too, so this covers both.
                                onInputChange={(_event, value) => setFieldValue('content_family_identifier', value)}
                                renderInput={props => (
                                    <MuiTextField
                                        {...props}
                                        label="Content Family Identifier"
                                        placeholder="-- Select a content family identifier"
                                    />
                                )}
                            />
                        )}
                    />

                    <Stack spacing={1}>
                        <SectionHeading>Related Playlists</SectionHeading>

                        {familyPlaylistTitles?.length ? (
                            familyPlaylistTitles.map(playlistTitle => (
                                <Typography key={playlistTitle} variant="body2">
                                    {playlistTitle}
                                </Typography>
                            ))
                        ) : (
                            <Typography variant="body2">None</Typography>
                        )}
                    </Stack>

                    <Stack spacing={1}>
                        <SectionHeading>Cohorts</SectionHeading>

                        {relatedCohorts?.length ? (
                            // Chips, not bare links: a playlist can hold forty or more cohorts, which
                            // read as one run of text with no boundary between names.
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {relatedCohorts.map(cohort => (
                                    <Chip
                                        key={cohort.id}
                                        component="a"
                                        clickable
                                        size="small"
                                        variant="outlined"
                                        label={cohort.name}
                                        href={`/admin/mba/cohorts?cohort-id=${cohort.id}`}
                                        target="_blank"
                                        rel="noopener"
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2">None</Typography>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
}
